import { keyframes, styled } from "goober";
import {
  createContext,
  useContext,
  useEffect,
  useId,
  useState,
  type MouseEvent,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { useActivityMenu } from "../hooks/useActivityMenu";
import { useCommandBar } from "../hooks/useCommandBar";
import { useIsNarrow } from "../hooks/useIsNarrow";
import { useKeyboardShortcuts } from "../hooks/useKeyboardShortcuts";
import { zIndexes } from "../lib/zIndexes";
import { ClickButton } from "./ClickButton";
import { isInAppNavigation } from "./Link";
import { Modal } from "./Modal";
import { MOTION_FAST_MS } from "./motion";
import { PanelToggleIcon } from "./PanelToggleIcon";

const enter = keyframes`
  from { transform: translateX(-100%); }
  to { transform: translateX(0); }
`;
const exit = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(-100%); }
`;

const FloatingButton = styled(ClickButton)`
  position: fixed;
  top: 50%;
  left: 0;
  transform: translateY(-50%);
  z-index: ${zIndexes.navigation};
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-primary);
  background: var(--surface-primary);
  border-radius: 0 16px 16px 0;
  outline: 0.5px solid var(--border-alpha);
  box-shadow: var(--elevation-low);
  &[aria-expanded="false"] .inner {
    width: 1px;
  }
  &:hover {
    background: linear-gradient(var(--alpha4)), var(--surface-primary);
  }
`;

const Drawer = styled(Modal)`
  position: fixed;
  inset: 0 auto 0 0;
  margin: 0;
  width: min(228px, calc(100vw / var(--ui-zoom, 1) - 56px));
  height: calc(100dvh / var(--ui-zoom, 1));
  max-width: none;
  max-height: none;
  border-radius: 0;
  background: var(--surface-secondary);
  transform: none;
  transition: none;
  animation: ${enter} var(--motion-base) var(--ease-out);
  &:has([data-closing="true"]) {
    animation: ${exit} ${MOTION_FAST_MS}ms var(--ease-in) forwards;
  }
  @starting-style {
    transform: none;
  }
`;

const Contents = styled("div")`
  height: 100%;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding-top: env(safe-area-inset-top, 0px);
  padding-bottom: env(safe-area-inset-bottom, 0px);
`;

const Header = styled("div")`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 68px;
  padding: 24px 12px 12px;
  flex-shrink: 0;
  &:has(nav[aria-label="Workspace"]) {
    padding-inline: 24px;
  }
`;

const Body = styled("div")`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

const NavigationContext = createContext<{
  container: HTMLDivElement | null;
  close: () => void;
} | null>(null);

export function MobileNavigationProvider({
  children,
}: {
  children: ReactNode;
}) {
  const isMobile = useIsNarrow(768);
  const { open: searchOpen } = useCommandBar();
  const { open: activityOpen, close: closeActivity } = useActivityMenu();
  const { open: shortcutsOpen } = useKeyboardShortcuts();
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const id = useId();
  if ((!isMobile || searchOpen || shortcutsOpen) && (open || mounted)) {
    setOpen(false);
    setMounted(false);
  }
  if (isMobile && activityOpen && !open && !searchOpen && !shortcutsOpen) {
    setOpen(true);
  }
  const close = () => {
    closeActivity();
    setOpen(false);
  };
  useEffect(() => {
    if (open) {
      // oxlint-disable-next-line react/set-state-in-effect -- Keep the dialog mounted until its closing animation finishes.
      setMounted(true);
      return;
    }
    if (!mounted) return;
    const timer = setTimeout(() => setMounted(false), MOTION_FAST_MS);
    return () => clearTimeout(timer);
  }, [open, mounted]);
  useEffect(() => {
    if (!isMobile) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (
        !event.defaultPrevented &&
        !event.isComposing &&
        (event.metaKey || event.ctrlKey) &&
        !event.shiftKey &&
        !event.altKey &&
        !event.repeat &&
        event.key === "\\"
      ) {
        event.preventDefault();
        if (open) closeActivity();
        setOpen((previous) => !previous);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isMobile, open, closeActivity]);
  return (
    <NavigationContext value={{ container, close }}>
      {children}
      {isMobile && (
        <FloatingButton
          data-no-pill
          aria-label="Open navigation"
          aria-expanded={open}
          aria-controls={id}
          onClick={() => setOpen(true)}
        >
          <PanelToggleIcon />
        </FloatingButton>
      )}
      <Drawer
        open={isMobile && mounted}
        onClose={close}
        id={id}
        aria-label="Navigation"
      >
        <Contents ref={setContainer} data-closing={!open} inert={!open} />
      </Drawer>
    </NavigationContext>
  );
}

export function MobileNavigation({
  header,
  children,
}: {
  header: ReactNode;
  children: ReactNode;
}) {
  const navigation = useContext(NavigationContext);
  if (!navigation?.container) return null;
  const closeLink = (event: MouseEvent) => {
    if (
      isInAppNavigation(event) &&
      event.target instanceof Element &&
      event.target.closest("a[href]")
    )
      navigation.close();
  };
  return createPortal(
    <>
      <Header>{header}</Header>
      <Body
        onMouseDownCapture={closeLink}
        onClickCapture={(event) => {
          closeLink(event);
          if (
            event.target instanceof Element &&
            event.target.closest("[data-navigation-close]")
          )
            navigation.close();
        }}
      >
        {children}
      </Body>
    </>,
    navigation.container
  );
}
